import { getAccessToken, getCurrentUser } from './authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'

const getBaseURL = () =>
  (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8081'

const getConfigServiceBaseUrl = () =>
  (import.meta.env.VITE_CONFIGURATION_SERVICE_URL as string) ||
  `${getBaseURL().replace(/\/$/, '')}/api/v1/configuration-service`

export interface WithdrawalSettings {
  withdrawalConditions: {
    minWithdrawalAmount: number
    gamesBeforeWithdrawal: number
    dailyWithdrawalLimit: number
    dailyWithdrawalAmountLimit: number
    autoWithdrawalFromFirstDeposit: string
    withdrawRequestCooldownSeconds: number
  }
  autoWithdrawalConditions: {
    autoApproveEnabled: boolean
    maxAutoApprovalAmount: number
    autoApprovalInterval: number
    autoPayoutEnabled: boolean
    maxWithdrawalPerTransaction: number
    forceDisableAmountChange: boolean
  }
  withdrawalBot: {
    shutdownTime: string
    activationTime: string
  }
}

interface WithdrawalSettingsResponse {
  success: boolean
  data?: {
    settings?: WithdrawalSettings
  }
}

export function getDefaultWithdrawalSettings(): WithdrawalSettings {
  return {
    withdrawalConditions: {
      minWithdrawalAmount: 100,
      gamesBeforeWithdrawal: 0,
      dailyWithdrawalLimit: 0,
      dailyWithdrawalAmountLimit: 0,
      autoWithdrawalFromFirstDeposit: 'disabled',
      withdrawRequestCooldownSeconds: 0,
    },
    autoWithdrawalConditions: {
      autoApproveEnabled: true,
      maxAutoApprovalAmount: 4999,
      autoApprovalInterval: 15,
      autoPayoutEnabled: true,
      maxWithdrawalPerTransaction: 30000,
      forceDisableAmountChange: false,
    },
    withdrawalBot: {
      shutdownTime: '23:40',
      activationTime: '08:00',
    },
  }
}

function resolveTenantId(): string {
  return getCurrentUser()?.tenantId || getRuntimeTenantIdSync() || localStorage.getItem('tenantId') || ''
}

function buildHeaders(tenantId: string): Record<string, string> {
  const token = getAccessToken()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(typeof window !== 'undefined' && window.location.hostname
      ? { 'X-Tenant-Domain': window.location.hostname }
      : {}),
  }
}

export async function getWithdrawalSettings(): Promise<WithdrawalSettings> {
  const tenantId = resolveTenantId()
  if (!tenantId) {
    return getDefaultWithdrawalSettings()
  }

  try {
    const res = await fetch(
      `${getConfigServiceBaseUrl()}/withdrawal/settings?tenantScope=${encodeURIComponent(tenantId)}&environment=all&_t=${Date.now()}`,
      {
        method: 'GET',
        headers: buildHeaders(tenantId),
        cache: 'no-store',
      }
    )

    if (!res.ok) {
      return getDefaultWithdrawalSettings()
    }

    const data = (await res.json()) as WithdrawalSettingsResponse
    const settings = data?.data?.settings
    if (!settings) {
      return getDefaultWithdrawalSettings()
    }

    return {
      ...getDefaultWithdrawalSettings(),
      ...settings,
      withdrawalConditions: {
        ...getDefaultWithdrawalSettings().withdrawalConditions,
        ...(settings.withdrawalConditions || {}),
      },
      autoWithdrawalConditions: {
        ...getDefaultWithdrawalSettings().autoWithdrawalConditions,
        ...(settings.autoWithdrawalConditions || {}),
      },
      withdrawalBot: {
        ...getDefaultWithdrawalSettings().withdrawalBot,
        ...(settings.withdrawalBot || {}),
      },
    }
  } catch (error) {
    console.error('Error fetching withdrawal settings:', error)
    return getDefaultWithdrawalSettings()
  }
}
